// Frontend contract model, derived once at the API seam (adaptJobs) so the list
// row, the detail card and the rules modules share one representation.
// Backend truth table: Docs/API/jobs.md "Contract block".

export const CONTRACT_STATE = Object.freeze({
  NO_CONTRACT: "no_contract",
  UNSIGNED: "unsigned",
  SIGNED: "signed",
  REVOKED: "revoked",
  REISSUE_REQUIRED: "reissue_required",
  DECLINED: "declined",
  DELIVERY_FAILED: "delivery_failed",
});

/**
 * Derive the frontend contract state from a list row's contract block.
 *
 * Never reads `reissue_available`: the list row carries only
 * `{status, reissue_required}`, so branching on it would make the list chip
 * disagree with the detail card.
 *
 * @param {Object|null} contract - { status, reissue_required, completed_at }
 * @returns {string} one of `CONTRACT_STATE`
 */
export const deriveContractStatus = (contract) => {
  if (!contract || !contract.status) return CONTRACT_STATE.NO_CONTRACT;

  const {
    status,
    reissue_required: reissueRequired,
    completed_at: completedAt,
  } = contract;

  if (status === "completed") return CONTRACT_STATE.SIGNED;
  if (status === "declined") return CONTRACT_STATE.DECLINED;
  if (status === "delivery_failed") return CONTRACT_STATE.DELIVERY_FAILED;

  if (status === "reset" || status === "voided") {
    // Fallback for payloads predating `reissue_required`: `reset` always comes
    // from a completed contract so it is always required; `voided` is required
    // only when the client already holds the signed copy.
    const required =
      reissueRequired ?? (status === "reset" || Boolean(completedAt));
    return required ? CONTRACT_STATE.REISSUE_REQUIRED : CONTRACT_STATE.REVOKED;
  }

  // "sent", plus any status this build does not know. A contract row exists, so
  // treating it as NO_CONTRACT would offer a send that cannot succeed.
  return CONTRACT_STATE.UNSIGNED;
};

export const canReissue = (status) =>
  status === CONTRACT_STATE.REVOKED ||
  status === CONTRACT_STATE.REISSUE_REQUIRED;

export const needsFreshContract = (status) =>
  status === CONTRACT_STATE.DECLINED ||
  status === CONTRACT_STATE.DELIVERY_FAILED;

export const canStartContract = (status) =>
  status === CONTRACT_STATE.NO_CONTRACT ||
  canReissue(status) ||
  needsFreshContract(status);

export const isContractWithClient = (status) =>
  status === CONTRACT_STATE.UNSIGNED || status === CONTRACT_STATE.SIGNED;

/**
 * Mirrors the backend's needs_attention contract set (Docs/API/jobs.md
 * "Tabs"). `revoked` is a deliberate act with an optional recovery, so it
 * stays out.
 */
export const isContractAnomaly = (status) =>
  status === CONTRACT_STATE.UNSIGNED ||
  status === CONTRACT_STATE.REISSUE_REQUIRED ||
  needsFreshContract(status);

export const hasSignedCopy = (contract) => Boolean(contract?.completed_at);
